import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { initials, ageFromDob, PRIORITY_STYLES } from "@/lib/helpers";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, MagnifyingGlass, Baby, WarningCircle, Info, PaperPlaneTilt } from "@phosphor-icons/react";
import { FileUpload } from "@/components/ui/file-upload";
import { FileImage } from "@/components/ui/file-image";
import { PlanGatedButton } from "@/components/PlanGating";
import IntakeManagementDialog from "@/components/IntakeManagementDialog";
import { usePermissions } from "@/hooks/usePermissions";

import { devLog } from "@/lib/devLog";
const ENROL_STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  not_registered: "bg-slate-100 text-slate-700 border-slate-200",
  registered: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const ENROL_STATUS_LABEL = {
  pending: "Pending",
  not_registered: "Not registered",
  registered: "Registered",
};

export default function StudentsPage() {
  const { activeBranch, activeRole } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showIntake, setShowIntake] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const branchId = activeBranch === "all" ? null : activeBranch;

  const load = async () => {
    setLoading(true);
    try {
      const [s, tt, br] = await Promise.all([
        api.get("/students", { params: { ...(branchId ? { branch_id: branchId } : {}), ...(search ? { search } : {}) } }),
        api.get("/therapy-types"),
        api.get("/branches"),
      ]);
      setStudents(s.data || []);
      setTherapyTypes(tt.data || []);
      setBranches(br.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeBranch]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  // Permission-gated: canCreate requires `limited` on `student` (matches
  // reception's "onboarding only" spec). Legacy role list kept as a fallback
  // for callers whose permissions endpoint hasn't loaded yet.
  const canCreate = can("student", "limited") || ["owner","admin","reception"].includes(activeRole);
  const canManageIntake = can("student", "limited") || ["owner","admin","reception","therapist"].includes(activeRole);

  const filteredStudents = statusFilter === "all"
    ? students
    : students.filter(s => (s.enrolment_status || "registered") === statusFilter);

  const counts = students.reduce((acc, s) => {
    const st = s.enrolment_status || "registered";
    acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="overline mb-1.5">Roster</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Students</h1>
          <p className="text-sm text-muted-foreground mt-1">{students.length} student{students.length!==1 && "s"} in view.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative w-full md:w-64">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search by name or Student ID…" value={search} onChange={(e)=>setSearch(e.target.value)} className="pl-9" data-testid="students-search" />
          </div>
          {canManageIntake && (
            <Button variant="outline" onClick={() => setShowIntake(true)} className="gap-1.5" data-testid="open-intake-mgmt">
              <PaperPlaneTilt size={16} weight="bold" />Send intake form
            </Button>
          )}
          {canCreate && (
            <PlanGatedButton flagKey="can_add_student" onClick={() => setShowNew(true)} className="gap-1.5" data-testid="new-student-btn"><Plus size={16} weight="bold" />Add student</PlanGatedButton>
          )}
        </div>
      </div>

      {/* Enrolment status filter tabs */}
      <div className="flex gap-1.5 flex-wrap" data-testid="enrol-status-tabs">
        {[
          ["all", "All", students.length],
          ["pending", "Pending", counts.pending || 0],
          ["not_registered", "Not registered", counts.not_registered || 0],
          ["registered", "Registered", counts.registered || 0],
        ].map(([k, label, count]) => (
          <button
            key={k}
            onClick={() => setStatusFilter(k)}
            data-testid={`enrol-tab-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              statusFilter === k ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-muted"
            }`}
          >
            {label} <span className="opacity-70">({count})</span>
          </button>
        ))}
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
        <Card>
          <CardContent className="p-0">
            {filteredStudents.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground text-sm flex flex-col items-center gap-2">
                <Baby size={32} weight="duotone" className="text-muted-foreground/40" />
                {statusFilter === "all" ? "No students yet." : `No ${ENROL_STATUS_LABEL[statusFilter]?.toLowerCase()} students.`}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filteredStudents.map(s => {
                  const therapies = therapyTypes.filter(t => (s.therapies || []).includes(t.id));
                  const branch = branches.find(b => b.id === s.branch_id);
                  const enrolStatus = s.enrolment_status || "registered";
                  return (
                    <button
                      key={s.id}
                      onClick={() => navigate(`/students/${s.id}`)}
                      data-testid={`student-row-${s.id}`}
                      className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-muted/50 text-left transition-colors"
                    >
                      <Avatar className="h-10 w-10">
                        {s.photo_file_id ? (
                          <FileImage
                            fileId={s.photo_file_id}
                            alt={s.name}
                            className="h-full w-full object-cover"
                            fallback={<AvatarFallback className="bg-primary/10 text-primary font-semibold">{initials(s.name)}</AvatarFallback>}
                          />
                        ) : (
                          <AvatarFallback className="bg-primary/10 text-primary font-semibold">{initials(s.name)}</AvatarFallback>
                        )}
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{s.name}</span>
                          {s.student_id && (
                            <span
                              className="font-mono text-[10px] font-semibold tabular-nums bg-primary/10 text-primary px-1.5 py-0.5 rounded"
                              data-testid={`student-id-${s.id}`}
                            >
                              {s.student_id}
                            </span>
                          )}
                          {enrolStatus !== "registered" && (
                            <Badge className={`${ENROL_STATUS_STYLES[enrolStatus]} text-[10px] uppercase tracking-wider border`}>{ENROL_STATUS_LABEL[enrolStatus]}</Badge>
                          )}
                          <span className="text-xs text-muted-foreground">· {ageFromDob(s.dob)} · {s.gender || "—"}</span>
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{s.diagnosis || "—"} · {branch?.name || "—"}</div>
                      </div>
                      <div className="hidden md:flex gap-1.5">
                        {therapies.slice(0, 3).map(t => (
                          <Badge key={t.id} variant="outline" style={{ borderColor: `${t.color}66`, color: t.color }} className="text-[10px]">{t.code}</Badge>
                        ))}
                      </div>
                      <Badge className={`${PRIORITY_STYLES[s.priority || "normal"]} text-[10px] uppercase tracking-wider`}>{s.priority}</Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <NewStudentDialog
        open={showNew} onOpenChange={setShowNew}
        therapyTypes={therapyTypes} branches={branches}
        defaultBranchId={branchId}
        onCreated={() => { setShowNew(false); load(); }}
      />
      <IntakeManagementDialog
        open={showIntake}
        onOpenChange={setShowIntake}
        branches={branches}
        defaultBranchId={branchId}
        onSubmitted={load}
      />
    </div>
  );
}

export function NewStudentDialog({ open, onOpenChange, therapyTypes, branches, defaultBranchId, onCreated, student }) {
  const isEdit = !!student;
  const [form, setForm] = useState({
    name: "", dob: "", gender: "", branch_id: "",
    diagnosis: "", medical_notes: "", priority: "normal",
    therapies: [], guardians: [{ name: "", phone: "", relation: "Parent", email: "" }],
    address: "", photo_file_id: null,
  });
  const [saving, setSaving] = useState(false);
  const [dupCheck, setDupCheck] = useState({ name_matches: [], email_conflict: false, email_matches: [] });
  const [emailShareAck, setEmailShareAck] = useState(false);

  // Debounced duplicate check — fires when the student name or the primary
  // guardian's email changes. Same-name matches surface as a subtle info
  // panel (creation is still allowed — real ops does see same-name kids);
  // email conflicts surface as a decision panel with Accept/Change buttons
  // because a shared guardian email is a legit multi-child-parent case.
  useEffect(() => {
    if (!open) return;
    const name = (form.name || "").trim();
    const email = (form.guardians[0]?.email || "").trim();
    if (!name && !email) {
      setDupCheck({ name_matches: [], email_conflict: false, email_matches: [] });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/students/duplicates-check", {
          params: {
            ...(name ? { name } : {}),
            ...(email ? { email } : {}),
            ...(isEdit && student?.id ? { exclude_id: student.id } : {}),
          },
        });
        setDupCheck(data);
        // If the email changed to a NEW unique one, clear the acknowledgement
        if (!data.email_conflict) setEmailShareAck(false);
      } catch (err) { devLog("duplicate check silent-fail", err); }
    }, 350);
    return () => clearTimeout(t);
  }, [open, form.name, form.guardians, isEdit, student?.id]);

  useEffect(() => {
    if (open) {
      if (isEdit && student) {
        // Pre-fill from existing student
        setForm({
          name: student.name || "",
          dob: student.dob || "",
          gender: student.gender || "",
          branch_id: student.branch_id || "",
          diagnosis: student.diagnosis || "",
          medical_notes: student.medical_notes || "",
          priority: student.priority || "normal",
          therapies: student.therapies || [],
          guardians: student.guardians?.length
            ? student.guardians.map(g => ({ name: g.name || "", phone: g.phone || "", relation: g.relation || "Parent", email: g.email || "", user_id: g.user_id }))
            : [{ name: "", phone: "", relation: "Parent", email: "" }],
          address: student.address || "",
          photo_file_id: student.photo_file_id || null,
        });
      } else {
        setForm(f => ({
          ...f, branch_id: defaultBranchId || (branches[0]?.id || ""), therapies: [], name: "", dob: "",
          gender: "", diagnosis: "", medical_notes: "", priority: "normal",
          guardians: [{ name: "", phone: "", relation: "Parent", email: "" }], address: "",
          photo_file_id: null,
        }));
      }
      setDupCheck({ name_matches: [], email_conflict: false, email_matches: [] });
      setEmailShareAck(false);
    }
  }, [open, defaultBranchId, branches, isEdit, student]);

  const toggleTherapy = (id) => setForm(f => ({
    ...f, therapies: f.therapies.includes(id) ? f.therapies.filter(x=>x!==id) : [...f.therapies, id]
  }));

  const setGuardian = (i, key, val) => setForm(f => {
    const gs = [...f.guardians]; gs[i] = { ...gs[i], [key]: val };
    return { ...f, guardians: gs };
  });

  const submit = async () => {
    if (!form.name || !form.branch_id) { toast.error("Name and branch required"); return; }
    if (dupCheck.email_conflict && !emailShareAck) {
      toast.error("Please confirm shared guardian email or change it");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/students/${student.id}`, form);
        toast.success("Student updated");
      } else {
        await api.post("/students", form);
        toast.success("Student added");
      }
      onCreated?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{isEdit ? "Edit student" : "Add new student"}</DialogTitle></DialogHeader>

        <div className="flex items-center gap-4 py-2">
          <FileUpload
            value={form.photo_file_id}
            onChange={(id) => setForm(f => ({ ...f, photo_file_id: id }))}
            entityType="student"
            entityId={student?.id}
            accept="image/jpeg,image/png,image/webp"
            variant="avatar"
            size={80}
          />
          <div className="text-xs text-muted-foreground">
            Profile photo <span className="text-[10px]">(optional, JPG/PNG/WEBP, ≤10 MB)</span>
          </div>
        </div>

        {dupCheck.name_matches.length > 0 && (
          <div
            className="flex gap-2 items-start rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-3 text-xs"
            data-testid="dup-name-info"
          >
            <Info size={16} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
            <div className="space-y-1">
              <div className="font-semibold">
                {dupCheck.name_matches.length} student{dupCheck.name_matches.length > 1 ? "s" : ""} with this name already exist{dupCheck.name_matches.length > 1 ? "" : "s"}. You can still add this one — just confirming you meant to.
              </div>
              <ul className="space-y-0.5">
                {dupCheck.name_matches.map(m => (
                  <li key={m.id} className="text-[11px]">
                    · <span className="font-medium">{m.name}</span>
                    {m.guardian_name && <> — {m.guardian_relation || "Guardian"} {m.guardian_name}</>}
                    {m.dob && <> · {ageFromDob(m.dob)}</>}
                    {m.branch_name && <> · {m.branch_name}</>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {dupCheck.email_conflict && (
          <div
            className={`flex gap-2 items-start rounded-md border p-3 text-xs ${emailShareAck ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-300 bg-amber-50 text-amber-900"}`}
            data-testid="dup-email-warn"
          >
            <WarningCircle size={16} weight="fill" className={`mt-0.5 shrink-0 ${emailShareAck ? "text-emerald-700" : "text-amber-700"}`} />
            <div className="space-y-2 flex-1">
              <div className="font-semibold">
                This email is already linked to {dupCheck.email_matches.length} other student record{dupCheck.email_matches.length > 1 ? "s" : ""}.
              </div>
              <div>
                If this is a <span className="font-medium">sibling</span> of the student{dupCheck.email_matches.length > 1 ? "s" : ""} below, that&rsquo;s fine — the parent will see all their children in one login. Otherwise please change the email.
              </div>
              <ul className="space-y-0.5">
                {dupCheck.email_matches.map(m => (
                  <li key={m.id} className="text-[11px]">
                    · <span className="font-medium">{m.name}</span>
                    {m.guardian_name && <> — {m.guardian_relation || "Guardian"} {m.guardian_name}</>}
                  </li>
                ))}
              </ul>
              {!emailShareAck && (
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-xs bg-white"
                    onClick={() => setEmailShareAck(true)}
                    data-testid="ack-email-shared"
                  >
                    Yes, same parent &mdash; keep email
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      setGuardian(0, "email", "");
                      setEmailShareAck(false);
                    }}
                    data-testid="clear-email"
                  >
                    Change email
                  </Button>
                </div>
              )}
              {emailShareAck && (
                <div className="text-[11px] font-medium">Confirmed &mdash; sibling record allowed. The parent will access all children with this email.</div>
              )}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Full name *"><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} data-testid="student-name" /></Field>
          <Field label="Date of birth"><Input type="date" value={form.dob} onChange={e=>setForm({...form, dob:e.target.value})} /></Field>
          <Field label="Gender">
            <Select value={form.gender} onValueChange={v=>setForm({...form, gender:v})}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Branch *">
            <Select value={form.branch_id} onValueChange={v=>setForm({...form, branch_id:v})}>
              <SelectTrigger data-testid="student-branch"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent className="bg-card">
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={form.priority} onValueChange={v=>setForm({...form, priority:v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card">
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Diagnosis"><Input value={form.diagnosis} onChange={e=>setForm({...form, diagnosis:e.target.value})} /></Field>
        </div>

        <div>
          <div className="text-sm font-medium mb-2">Therapies</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {therapyTypes.map(t => (
              <label key={t.id} className={`flex items-center gap-2 p-2 border rounded-md cursor-pointer hover:bg-muted/40 ${form.therapies.includes(t.id) ? "border-primary bg-primary/5" : "border-border"}`}>
                <Checkbox checked={form.therapies.includes(t.id)} onCheckedChange={() => toggleTherapy(t.id)} />
                <span className="text-sm">{t.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <div className="text-sm font-medium">Primary guardian</div>
          <div className="grid md:grid-cols-2 gap-3">
            <Input placeholder="Name" value={form.guardians[0].name} onChange={e=>setGuardian(0, "name", e.target.value)} />
            <Input placeholder="Phone" value={form.guardians[0].phone} onChange={e=>setGuardian(0, "phone", e.target.value)} />
            <Input placeholder="Relation" value={form.guardians[0].relation} onChange={e=>setGuardian(0, "relation", e.target.value)} />
            <Input placeholder="Email (optional)" value={form.guardians[0].email} onChange={e=>setGuardian(0, "email", e.target.value)} />
          </div>
        </div>

        <Field label="Medical notes"><Textarea value={form.medical_notes} onChange={e=>setForm({...form, medical_notes:e.target.value})} rows={3} /></Field>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-student">{saving ? "Saving…" : (isEdit ? "Save changes" : "Add student")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
